import { useBunja } from 'bunja/react';
import { useTranslations } from 'next-intl';
import { useFormContext, useWatch } from 'react-hook-form';

import { ControlClearableNumberInputWithFrame } from '@flipster/features/src/input/clearable-number-input';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { FormItem } from '@flipster/ui';
import { getCustomFormError } from '@flipster/utils/error';

import { TpslSchema } from '@web/features/modals/TimeTriggerTpslModal/bunja/validation';
import styles from '@web/features/modals/TimeTriggerTpslModal/tpslModal.module.scss';

import { tpslModalFormBunja, tpslModalSegmentBunja } from '../../tpsl-modal-bunja';

interface Props {
  entryPrice?: string;
  pricePrecision: number;
}

const TriggerPriceInput = ({ entryPrice, pricePrecision }: Props) => {
  const transAssets = useTranslations('Assets.r250401');
  const transTrade = useTranslations('Trade.r250401');
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<TpslSchema>();

  const triggerPrice = useWatch({ control, name: 'triggerPrice' }) || '0';
  const { calcFromEntryPercentage } = useBunja(tpslModalFormBunja);
  const fromMarketPrice = calcFromEntryPercentage(triggerPrice, entryPrice || '0');

  const { trackTpslPriceClearClick, trackTpslPriceEditClick, trackTpslPriceResetClick } =
    useBunja(tpslModalSegmentBunja);

  return (
    <div className={styles.triggerPriceWrapper}>
      <FormItem
        label={transAssets('Trigger_price')}
        error={getCustomFormError(errors.triggerPrice)}
        className={styles.triggerPrice}
        inheritLabel
      >
        <ControlClearableNumberInputWithFrame
          name='triggerPrice'
          maxLength={12}
          precision={pricePrecision}
          onChange={(value) => {
            setValue('basePrice', value);
          }}
          onFocus={trackTpslPriceEditClick}
          onClearClick={trackTpslPriceClearClick}
          placeholder={transTrade('Enter_price')}
          customStyle
          className={css({
            h: 37,
            p: 0,
            border: 'none',
            textStyle: 'Title/M',
            caretColor: 'content.accent.info',
          })}
          suffix={
            <div className={hstack({ gap: 4, textStyle: 'Text/S/Monospace/Regular' })}>
              <Formatted.Percent
                withColor
                withPlus
                toFixed
                precision={2}
                method='round-half-up'
                value={fromMarketPrice}
              />
              <TextButton
                onClick={() => {
                  trackTpslPriceResetClick();
                  setValue('triggerPrice', entryPrice);
                }}
                label={transTrade('From_entry')}
                color='neutral'
                size='small'
              />
            </div>
          }
        />
      </FormItem>
    </div>
  );
};

export default TriggerPriceInput;
